import { Schema } from "effect"
import { Evidence, Hold, RecordMeta } from "../common"
import {
  AgentId,
  AreaId,
  GoalId,
  InitiativeId,
  IntegrationId,
  KpiId,
  Markdown,
  MemoryItemId,
  Millis,
  Rank,
  ShortRef,
  Slug,
  Why,
} from "../ids"

// #region kpi
export const Comparator = Schema.Literals([">=", ">", "<=", "<", "=="])
/** A threshold on one KPI. Targets live on goals and bets, never on the KPI (a KPI can serve two goals). */
export const MetricCondition = Schema.Struct({
  kpiId: KpiId,
  op: Comparator,
  value: Schema.Finite,
  minN: Schema.NullOr(Schema.Int), // minimum sample before judging (low-volume reality)
})

export const KpiSource = Schema.TaggedUnion({
  manual: {},
  /** Phase 3 connectors: Stripe/RevenueCat, PostHog, App Store Connect, Search Console. */
  connector: { integrationId: IntegrationId, query: Schema.JsonObject },
  /** Derived, e.g. conversion = paid / trials. */
  formula: { expression: Schema.String, inputs: Schema.Array(KpiId) },
})

/** An entity, not a graph node: rendered as chips/sparklines inside goal nodes and area headers. */
export const Kpi = Schema.Struct({
  ...RecordMeta,
  id: KpiId,
  ref: ShortRef, // K-3
  areaId: Schema.NullOr(AreaId), // null = project-level
  key: Slug, // trial_starts_organic → "trial-starts-organic"
  name: Schema.String,
  unit: Schema.String, // "users", "%", "€"
  direction: Schema.Literals(["up", "down"]), // which way is good
  role: Schema.Literals(["input", "lagging", "health"]),
  source: KpiSource,
  cadence: Schema.Literals(["hourly", "daily", "weekly"]),
  /** Data latency (GSC ~3 d, ASC ~2 d): samples inside it are provisional and never auto-judged. */
  latencyDays: Schema.Int,
  /** ASC hides counts < 5: below this value show "<n" and never auto-judge. */
  minReportable: Schema.NullOr(Schema.Int),
  /** Metric tree: this KPI feeds those (input KPI → goal KPI). Not edges. */
  rollsUpTo: Schema.Array(KpiId),
  baseline: Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis })),
  /** Latest sample, folded from KpiSampleRecorded. History lives in the kpi_samples projection. */
  latest: Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis, provisional: Schema.Boolean })),
})

export const KpiSample = Schema.Struct({
  at: Millis, // the period the value describes, not when it was fetched
  value: Schema.Finite,
  fetchedAt: Millis,
  provisional: Schema.Boolean,
  n: Schema.NullOr(Schema.Int),
})
// #endregion kpi

// #region goal
/** Shared by goals, initiatives and tasks: what every view and the context assembler need. */
export const NodeBase = {
  ...RecordMeta,
  ref: ShortRef,
  title: Schema.String,
  /** One or two sentences: why this exists. Required; shown in the inspector, re-injected into every run's context. */
  why: Why,
  body: Markdown,
  rank: Rank, // priority among siblings; people drag it
  hold: Hold,
  tags: Schema.Array(Slug), // memory triggers and filters
} as const

export const GoalSpec = Schema.TaggedUnion({
  /** "100 paid users by Dec 31": a KPI and a target. */
  metric: { target: MetricCondition },
  /** "Dictly 2.0 on the App Store": a checkable definition of done. */
  milestone: { definitionOfDone: Schema.NonEmptyArray(Schema.String) },
})

export const Goal = Schema.Struct({
  ...NodeBase,
  id: GoalId,
  status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
  deadline: Schema.NullOr(Millis),
  spec: GoalSpec,
  /** Supporting KPIs shown in the goal node (input KPIs rolled up from areas). */
  kpiIds: Schema.Array(KpiId),
})
// #endregion goal

// #region initiative
export const Horizon = Schema.Literals(["now", "next", "later"])
export const BetVerdictValue = Schema.Literals([
  "worked",
  "promising",
  "inconclusive",
  "no_effect",
  "harmful",
])

/** Exploratory work. Fields exist from v1; evaluation behaviour (kill criteria, verdicts) is Phase 3. */
export const Bet = Schema.Struct({
  hypothesis: Schema.String,
  inputKpiId: KpiId, // required
  laggingKpiId: Schema.NullOr(KpiId),
  success: Schema.Struct({ condition: MetricCondition, evalAfterDays: Schema.Int }),
  kill: Schema.Array(Schema.Struct({ condition: MetricCondition, byDays: Schema.Int })),
  /** Shape Up appetite = circuit breaker. `runs` bounds worker attempts across the bet. */
  appetite: Schema.Struct({ calendarDays: Schema.Int, runs: Schema.Int }),
  alternativesConsidered: Schema.Array(InitiativeId),
  /** Set only by an evaluator agent, never by a worker or the bet's own owner agent. */
  verdict: Schema.NullOr(
    Schema.Struct({ value: BetVerdictValue, by: AgentId, at: Millis, evidence: Schema.Array(Evidence) }),
  ),
  /** A killed bet can't be retried without evidence contradicting this learning. */
  learningId: Schema.NullOr(MemoryItemId),
})

/** Known work: a deliverable with a definition of done. Can link a KPI without being judged on it. */
export const Workstream = Schema.Struct({
  definitionOfDone: Schema.NonEmptyArray(Schema.String),
  targetDate: Schema.NullOr(Millis),
  supportsKpiId: Schema.NullOr(KpiId),
})

export const InitiativeShape = Schema.TaggedUnion({ bet: Bet.fields, workstream: Workstream.fields })
export const InitiativeStatus = Schema.Literals([
  "shaping",
  "active",
  "paused",
  "done",
  "killed",
  "abandoned",
])

export const Initiative = Schema.Struct({
  ...NodeBase,
  id: InitiativeId,
  /** Goals it serves. The first is primary: it defines ancestry and context. "Launch campaign" serves two goals. */
  goalIds: Schema.NonEmptyArray(GoalId),
  areaId: AreaId, // owning area = lane on the canvas
  ownerAgentId: Schema.NullOr(AgentId), // the role agent who breaks it down
  horizon: Horizon, // planning intent; only `now` initiatives get tasks
  status: InitiativeStatus, // lifecycle; `active` counts against area and project WIP
  shape: InitiativeShape,
  supersedes: Schema.NullOr(InitiativeId),
  startedAt: Schema.NullOr(Millis),
  closedAt: Schema.NullOr(Millis),
})
// #endregion initiative
